/**
 * Plan-scope helpers (2026-07 Lens/Reader unbundling).
 *
 * The backend's GET /me may include:
 *   - planScope: 'combo' | 'lens' | 'reader' (bonus/trial periods are always 'combo')
 *   - readerEffectivePlan: the plan as seen from the READER side — equals `plan`
 *     when the scope covers Reader, otherwise 'free'
 *   - productId / productName: purchased product identity (productName is a
 *     per-language Record, e.g. { zh: '基础版', en: 'Basic' })
 *
 * OLD backends don't send these fields. The local fallback below reproduces
 * the server rule so a new frontend against an old backend behaves exactly
 * like today (missing scope == 'combo' == plan applies to Reader).
 *
 * Single source of truth for Reader-side plan gating: every "is this user
 * paid?" check in the Reader app must read `user.readerPaid` / `user.readerPlan`
 * (derived here), NOT the raw `user.plan` — a Lens-only subscriber has a paid
 * `plan` but is 'free' on the Reader side.
 */

/**
 * Local fallback for readerEffectivePlan when the backend doesn't send it.
 * Missing/unknown scope is treated as 'combo' (matches pre-unbundling
 * behavior — zero regression against old backends).
 */
export function computeReaderEffectivePlan(plan, planScope) {
  const p = plan || 'free';
  if (!planScope || planScope === 'combo' || planScope === 'reader') return p;
  return 'free';
}

/**
 * Normalize a raw user record (fresh /me payload fields or a cached USER_KEY
 * object possibly written by an older build) into one carrying the derived
 * Reader-side fields:
 *   - planScope (null == combo semantics)
 *   - readerEffectivePlan (server value verbatim when present, else computed)
 *   - readerPlan  (alias of readerEffectivePlan — the single gating source)
 *   - readerPaid  (readerPlan !== 'free')
 *   - productId / productName (null when absent)
 *
 * Cache objects written before this field existed lack planScope /
 * readerEffectivePlan → fallback computes combo semantics from `plan`,
 * which is exactly the pre-unbundling behavior.
 */
export function normalizeUserPlanFields(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const planScope = raw.planScope ?? null;
  const readerEffectivePlan =
    raw.readerEffectivePlan ?? computeReaderEffectivePlan(raw.plan, planScope);
  const readerPlan = readerEffectivePlan;
  return {
    ...raw,
    planScope,
    readerEffectivePlan,
    productId: raw.productId ?? null,
    productName: raw.productName ?? null,
    readerPlan,
    readerPaid: readerPlan !== 'free',
  };
}
